<!-- ============================================================
 * ContactInquiryDrawer.vue
 * 초기 도입 문의 드로어 — 화면 왼쪽에서 슬라이드 (§3.2)
 *
 * Props:
 *   isOpen (Boolean) — 열림/닫힘 제어 (부모에서 관리)
 *
 * Events:
 *   close                — 닫기 요청 (부모에서 isOpen 을 false 로)
 *   submitted (message)  — 접수 완료 (서버 응답 메시지를 부모로 전달)
 * ============================================================ -->
<template>
    <div>
        <!-- 배경 오버레이 -->
        <transition name="cid-bg">
            <div
                v-if="isOpen"
                class="cid-backdrop"
                aria-hidden="true"
                @click="handleClose"
            />
        </transition>

        <!-- 드로어 패널 (왼쪽) -->
        <aside
            class="cid-panel"
            :class="{ 'cid-panel--open': isOpen }"
            role="dialog"
            aria-modal="true"
            aria-label="초기 도입 문의 폼"
        >
            <!-- ── 헤더 ───────────────────────────────────── -->
            <div class="cid-hd">
                <div class="cid-hd__info">
                    <h2 class="cid-hd__title">초기 도입 문의</h2>
                    <p class="cid-hd__desc">안녕하세요 😊 레코스 오픈배지에 관심 주셔서 감사합니다. 아래 항목을 작성해주시면 순차적으로 메일 회신드리겠습니다.</p>
                </div>
                <button type="button" class="cid-close-btn" aria-label="닫기" @click="handleClose">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <!-- ── 바디 (스크롤 가능) ─────────────────────── -->
            <div ref="scrollBody" class="cid-body">
                <form novalidate @submit.prevent>

                    <!-- 허니팟(스팸 봇 차단): 사용자 눈에 안 보이는 가짜 필드. 값이 채워지면 서버가 봇으로 간주해 무시한다.
                         name은 브라우저 자동완성이 인식하지 못하도록 비표준 토큰을 쓴다(website/url/email 등 금지) — 정상 유저 오탐 방지. -->
                    <input
                        v-model="website"
                        type="text"
                        name="contact_extra"
                        tabindex="-1"
                        autocomplete="off"
                        aria-hidden="true"
                        style="position:absolute; left:-9999px; width:1px; height:1px; opacity:0;"
                    />

                    <!-- ════════════════════════════════════
                     §3.2-A 기본 정보 (전체 필수)
                    ════════════════════════════════════ -->
                    <section class="cid-sec">
                        <h3 class="cid-sec__title">
                            기본 정보
                            <span class="cid-badge cid-badge--req">필수</span>
                        </h3>

                        <!-- 회사명 · 이름 (2열) -->
                        <div class="cid-grid">
                            <!-- 회사명 -->
                            <div class="cid-field" :class="{ 'cid-field--err': errors.companyName }">
                                <label class="cid-lbl">회사명</label>
                                <input
                                    v-model.trim="form.companyName"
                                    maxlength="200"
                                    class="cid-inp"
                                    type="text"
                                    placeholder="회사명을 입력하세요."
                                    @input="clearErr('companyName')"
                                />
                                <span v-if="errors.companyName" class="cid-errmsg">{{ errors.companyName }}</span>
                            </div>

                            <!-- 이름 -->
                            <div class="cid-field" :class="{ 'cid-field--err': errors.contactName }">
                                <label class="cid-lbl">이름</label>
                                <input
                                    v-model.trim="form.contactName"
                                    maxlength="100"
                                    class="cid-inp"
                                    type="text"
                                    placeholder="이름을 작성해주세요."
                                    @input="clearErr('contactName')"
                                />
                                <span v-if="errors.contactName" class="cid-errmsg">{{ errors.contactName }}</span>
                            </div>

                            <!-- 직책 -->
                            <div class="cid-field" :class="{ 'cid-field--err': errors.position }">
                                <label class="cid-lbl">직책</label>
                                <input
                                    v-model.trim="form.position"
                                    maxlength="100"
                                    class="cid-inp"
                                    type="text"
                                    placeholder="직책을 입력하세요."
                                    @input="clearErr('position')"
                                />
                                <span v-if="errors.position" class="cid-errmsg">{{ errors.position }}</span>
                            </div>

                            <!-- 연락처 -->
                            <div class="cid-field" :class="{ 'cid-field--err': errors.phone }">
                                <label class="cid-lbl">연락처</label>
                                <input
                                    v-model.trim="form.phone"
                                    maxlength="50"
                                    class="cid-inp"
                                    type="tel"
                                    placeholder="연락 가능한 연락처를 작성해주세요."
                                    @input="clearErr('phone')"
                                    @blur="form.phone = formatPhoneKR(form.phone)"
                                />
                                <span v-if="errors.phone" class="cid-errmsg">{{ errors.phone }}</span>
                            </div>
                        </div>

                        <!-- 이메일 (전체 너비) -->
                        <div class="cid-field" :class="{ 'cid-field--err': errors.email }">
                            <label class="cid-lbl">이메일</label>
                            <input
                                v-model.trim="form.email"
                                maxlength="300"
                                class="cid-inp"
                                type="email"
                                placeholder="example@company.com"
                                @input="clearErr('email')"
                            />
                            <span v-if="errors.email" class="cid-errmsg">{{ errors.email }}</span>
                        </div>

                        <!-- 산업 분야 (라디오 버튼 그리드) -->
                        <div class="cid-field" :class="{ 'cid-field--err': errors.industryType }">
                            <label class="cid-lbl">산업 분야</label>
                            <div class="cid-opt-wrap cid-opt-wrap--4col">
                                <button
                                    v-for="opt in INDUSTRY_OPTIONS"
                                    :key="opt"
                                    type="button"
                                    class="cid-opt cid-opt--radio"
                                    :class="{ 'cid-opt--on': form.industryType === opt }"
                                    @click="form.industryType = opt; clearErr('industryType')"
                                >{{ opt }}</button>
                            </div>
                            <span v-if="errors.industryType" class="cid-errmsg">{{ errors.industryType }}</span>
                        </div>
                    </section>

                    <!-- ════════════════════════════════════
                     §3.2-B 문의 내용 (선택)
                    ════════════════════════════════════ -->
                    <section class="cid-sec">
                        <h3 class="cid-sec__title">
                            문의 내용
                            <span class="cid-badge cid-badge--opt">선택</span>
                        </h3>

                        <!-- 상세 내용 -->
                        <div class="cid-field">
                            <label class="cid-lbl">상세 내용</label>
                            <textarea
                                v-model="form.description"
                                maxlength="4000"
                                class="cid-inp cid-inp--ta"
                                rows="4"
                                placeholder="문의하실 내용을 자유롭게 입력해 주세요."
                            />
                        </div>

                        <!-- 진행 사업 -->
                        <div class="cid-field">
                            <label class="cid-lbl">진행 사업</label>
                            <input
                                v-model.trim="form.businessName"
                                maxlength="500"
                                class="cid-inp"
                                type="text"
                                placeholder="오픈배지가 필요한 사업을 작성해주세요."
                            />
                        </div>

                        <!-- 도입 항목 (다중 선택) -->
                        <div class="cid-field">
                            <label class="cid-lbl">
                                도입 항목
                                <span class="cid-badge cid-badge--multi">중복 선택</span>
                            </label>
                            <div class="cid-opt-wrap cid-opt-wrap--2col">
                                <button
                                    v-for="item in INTRO_NEED_OPTIONS"
                                    :key="item.code"
                                    type="button"
                                    class="cid-opt cid-opt--check"
                                    :class="{ 'cid-opt--on': form.introNeeds.includes(item.code) }"
                                    @click="toggleNeed(item.code)"
                                >{{ item.label }}</button>
                            </div>
                        </div>
                    </section>

                    <!-- ════════════════════════════════════
                     §3.2-C 도입 의향 (필수, 단일선택)
                    ════════════════════════════════════ -->
                    <section class="cid-sec">
                        <h3 class="cid-sec__title">
                            도입 의향
                            <span class="cid-badge cid-badge--req">필수</span>
                        </h3>
                        <div class="cid-field" :class="{ 'cid-field--err': errors.intent }">
                            <div class="cid-opt-col">
                                <button
                                    v-for="opt in INTENT_OPTIONS"
                                    :key="opt.code"
                                    type="button"
                                    class="cid-opt cid-opt--radio cid-opt--block"
                                    :class="{ 'cid-opt--on': form.intent === opt.code }"
                                    @click="form.intent = opt.code; clearErr('intent')"
                                >{{ opt.label }}</button>
                            </div>
                            <span v-if="errors.intent" class="cid-errmsg">{{ errors.intent }}</span>
                        </div>
                    </section>

                    <!-- ════════════════════════════════════
                     §3.2-D 개인정보 수집 동의 (필수)
                    ════════════════════════════════════ -->
                    <section class="cid-sec cid-sec--last">
                        <div class="cid-field" :class="{ 'cid-field--err': errors.agreePrivacy }">
                            <div class="cid-agree">
                                <button
                                    type="button"
                                    class="cid-agree__box"
                                    :class="{ 'cid-agree__box--on': form.agreePrivacy }"
                                    aria-label="개인정보 수집 및 이용 동의"
                                    @click="form.agreePrivacy = !form.agreePrivacy; clearErr('agreePrivacy')"
                                >
                                    <svg v-if="form.agreePrivacy" width="12" height="10" viewBox="0 0 12 10" fill="none">
                                        <path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                                <p class="cid-agree__txt">
                                    <a :href="privacyHref" target="_blank" rel="noopener noreferrer" class="cid-agree__link">개인정보 수집 및 이용</a>에
                                    동의합니다. <span class="cid-badge cid-badge--req">필수</span>
                                </p>
                            </div>
                            <span v-if="errors.agreePrivacy" class="cid-errmsg">{{ errors.agreePrivacy }}</span>
                        </div>
                    </section>

                </form>
            </div><!-- /cid-body -->

            <!-- ── 푸터 (제출 버튼) ───────────────────────── -->
            <div class="cid-ft">
                <!-- 서버/네트워크 오류 안내 (§3.4 재시도 유도) -->
                <p v-if="submitError" class="cid-submit-err" role="alert">{{ submitError }}</p>
                <button
                    type="button"
                    class="cid-submit"
                    :disabled="isSubmitting"
                    @click="handleSubmit"
                >
                    <span v-if="isSubmitting" class="cid-submit__spinner" aria-hidden="true" />
                    {{ isSubmitting ? '제출 중…' : '문의 제출' }}
                </button>
            </div>
        </aside>
    </div>
</template>

<script>
import { resolveApiErrorMessage } from '@/utils/apiError';
import { formatPhoneKR } from '@/utils/phone';

// ── 선택지 상수 ─────────────────────────────────────────────
const INDUSTRY_OPTIONS = ['대학교', '전문대학', '초중고등학교', '기업', '공공기관', '민간기관', '협회', '기타'];

/** 도입 항목: 사용자 노출 문구(label) · DB 코드(code) 매핑 (§7.1) */
const INTRO_NEED_OPTIONS = [
    { code: 'BADGE_DESIGN',         label: '오픈배지 디자인이 필요해요.' },
    { code: 'PLATFORM_INTEGRATION', label: '기존 플랫폼과 연동이 필요해요.' },
    { code: 'SMALL_QUANTITY',       label: '발행 규모는 1,000개 이하 소량이에요.' },
    { code: 'LARGE_QUANTITY',       label: '발행 규모는 2,000개 이상 대량이에요.' },
    { code: 'ENTERPRISE',           label: '엔터프라이즈형(외부 비공개) 도입을 원해요.' },
    { code: 'PAPER_CERT',           label: '보유 중인 종이 증명서 양식으로 발행하고 싶어요.' },
];

/** 도입 의향: 사용자 노출 문구 · DB 코드 매핑 (§3.2) */
const INTENT_OPTIONS = [
    { code: 'CONSULT_AND_MATERIAL', label: '네, 자료+상담 원해요' },
    { code: 'MATERIAL_ONLY',        label: '네, 자료만 받을래요' },
    { code: 'NO_INTENTION',         label: '아직 도입 의향은 없어요' },
];

/** 폼 초기 상태 팩토리 */
function makeForm() {
    return {
        companyName:  '',
        contactName:  '',
        position:     '',
        phone:        '',
        email:        '',
        industryType: null,  // INDUSTRY_OPTIONS 중 1개
        description:  '',
        businessName: '',
        introNeeds:   [],    // INTRO_NEED_OPTIONS code 배열
        intent:       null,  // INTENT_OPTIONS code
        agreePrivacy: false,
    };
}

export default {
    name: 'ContactInquiryDrawer',

    props: {
        isOpen: { type: Boolean, default: false },
    },

    data() {
        return {
            INDUSTRY_OPTIONS,
            INTRO_NEED_OPTIONS,
            INTENT_OPTIONS,
            form:        makeForm(),
            website:     '',    // 허니팟(스팸 봇 차단) — 정상 사용자는 비움
            errors:      {},
            isSubmitting: false,
            submitError:  '',   // 서버/네트워크 오류 안내 (§3.4)
        };
    },

    computed: {
        /** 개인정보처리방침 링크 (기존 페이지 패턴 재사용) */
        privacyHref() {
            // PrivacyPage 라우트는 '/:lang/privacy'로 lang 파라미터가 필수 — 누락 시 href가 깨진다.
            const lang = this.$route.params.lang || 'kor';
            return this.$router.resolve({ name: 'PrivacyPage', params: { lang } }).href;
        },
    },

    watch: {
        isOpen(open) {
            if (open) {
                // 열릴 때 바디 맨 위로 스크롤
                this.$nextTick(() => {
                    if (this.$refs.scrollBody) this.$refs.scrollBody.scrollTop = 0;
                });
            } else {
                // 닫힐 때 폼 초기화 (다음 렌더 후 처리해 사라지는 애니메이션과 충돌 방지)
                setTimeout(() => this._resetForm(), 300);
            }
        },
    },

    mounted() {
        this._onKeydown = e => { if (e.key === 'Escape' && this.isOpen) this.handleClose(); };
        document.addEventListener('keydown', this._onKeydown);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this._onKeydown);
    },

    methods: {
        // ── 닫기 ──────────────────────────────────────────
        handleClose() {
            if (this.isSubmitting) return;
            this.$emit('close');
        },

        // ── 에러 초기화 ───────────────────────────────────
        clearErr(field) {
            if (this.errors[field]) this.$set(this.errors, field, '');
        },

        // 연락처 자동 하이픈 보정 (blur 시 호출) — 어드민과 동일 정책
        formatPhoneKR,

        // ── 도입 항목 토글 (다중 선택) ───────────────────
        toggleNeed(code) {
            const i = this.form.introNeeds.indexOf(code);
            if (i === -1) this.form.introNeeds.push(code);
            else          this.form.introNeeds.splice(i, 1);
        },

        // ── 유효성 검사 ───────────────────────────────────
        _validate() {
            const e = {};
            if (!this.form.companyName)  e.companyName  = '회사명을 입력해 주세요.';
            if (!this.form.contactName)  e.contactName  = '이름을 입력해 주세요.';
            if (!this.form.position)     e.position     = '직책을 입력해 주세요.';
            if (!this.form.phone)        e.phone        = '연락처를 입력해 주세요.';
            if (!this.form.email) {
                e.email = '이메일을 입력해 주세요.';
            } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
                e.email = '올바른 이메일 형식을 입력해 주세요.';
            }
            if (!this.form.industryType) e.industryType = '산업 분야를 선택해 주세요.';
            if (!this.form.intent)       e.intent       = '도입 의향을 선택해 주세요.';
            if (!this.form.agreePrivacy) e.agreePrivacy = '개인정보 수집 및 이용에 동의해 주세요.';

            this.errors = e;
            return Object.keys(e).length === 0;
        },

        // ── 제출 (§3.2 / §3.4) ────────────────────────────
        async handleSubmit() {
            if (!this._validate()) {
                // 첫 오류 항목으로 스크롤
                this.$nextTick(() => {
                    const el = this.$refs.scrollBody?.querySelector('.cid-field--err');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
                return;
            }

            this.submitError = '';
            this.isSubmitting = true;
            try {
                // 도입 문의 접수 — 경로(홈페이지)·동의 시각은 서버 자동 처리(§4.2/§7)
                const payload = { ...this.form, introNeeds: [...this.form.introNeeds], website: this.website };
                const res = await this.$store.dispatch('inquery/submitIntroInquiry', payload);

                // DB 저장 성공·비상 메일 성공 모두 접수 완료 동일 처리(§3.4)
                const message = res?.message || '문의가 접수되었습니다. 빠른 시일 내 연락드리겠습니다.';
                this.$emit('submitted', message);
            } catch (err) {
                // 접수 미완료 — 화면에 오류 안내 후 재시도 유도(§3.4)
                console.error('[도입 문의 제출 오류]', err);
                this.submitError = resolveApiErrorMessage(err, '문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
            } finally {
                this.isSubmitting = false;
            }
        },

        // ── 폼 초기화 ─────────────────────────────────────
        _resetForm() {
            this.form        = makeForm();
            this.errors      = {};
            this.submitError = '';
        },
    },
};
</script>

<style lang="scss" scoped>
@import './inquiry-tokens';

// ─── 로컬 별칭 ──────────────────────────────────────────────
$orange    : $inq-orange;
$blue-dark : $inq-blue-dark;
$border    : $inq-border-input;
$focus-bdr : $inq-focus-bdr;
$focus-shd : $inq-focus-shd;
$active-bdr: $inq-active-bdr;
$active-bg : $inq-active-bg;
$active-clr: $inq-active-clr;
$err-clr   : $inq-err-clr;
$panel-bg  : $inq-panel-bg;

// ─── 오버레이 ───────────────────────────────────────────────
.cid-backdrop {
    position: fixed;
    inset:    0;
    background: $inq-backdrop;
    z-index:  9998;
    cursor:   pointer;
}
.cid-bg-enter-active,
.cid-bg-leave-active { transition: opacity .22s ease; }
.cid-bg-enter,
.cid-bg-leave-to     { opacity: 0; }

// ─── 패널 ───────────────────────────────────────────────────
.cid-panel {
    position:       fixed;
    top:            0;
    left:           0;
    bottom:         0;
    width:          clamp(680px, 50vw, 840px);
    background:     $panel-bg;
    box-shadow:     6px 0 48px $inq-shadow;
    z-index:        9999;
    display:        flex;
    flex-direction: column;
    transform:      translateX(-105%);
    transition:     transform .28s cubic-bezier(.4, 0, .2, 1);

    &--open { transform: translateX(0); }
}

// ─── 헤더 ───────────────────────────────────────────────────
.cid-hd {
    flex-shrink:     0;
    display:         flex;
    justify-content: space-between;
    align-items:     flex-start;
    padding:         20px 40px 16px;
    border-bottom:   1px solid $inq-border-divider;
    background:      #fff;
}

// _common.scss 전역 h 태그에 color !important 가 있어 명시적으로 override
.cid-hd__title {
    font-size:   2.1rem;
    font-weight: 800;
    color:       $blue-dark !important;
    margin:      0 0 6px;
    line-height: 1.3;
}

.cid-hd__desc {
    font-size:   1.35rem;
    color:       $inq-text-sub;
    margin:      0;
    line-height: 1.6;
}

.cid-close-btn {
    flex-shrink:     0;
    width:           36px;
    height:          36px;
    border:          none;
    border-radius:   8px;
    background:      $inq-ctrl-bg;
    color:           #667085;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    margin-top:      2px;
    transition:      background .15s ease, color .15s ease;
    &:hover { background: $inq-ctrl-hv; color: $inq-text-primary; }
}

// ─── 바디 ───────────────────────────────────────────────────
.cid-body {
    flex:       1 1 auto;
    overflow-y: auto;
    padding:    28px 40px 20px;
}

// ─── 섹션 ───────────────────────────────────────────────────
.cid-sec {
    display:        flex;
    flex-direction: column;
    gap:            16px;
    margin-bottom:  36px;

    &--last { margin-bottom: 0; }
}

.cid-sec__title {
    display:     flex;
    align-items: center;
    gap:         10px;
    font-size:   1.7rem;
    font-weight: 800;
    color:       $inq-text-primary !important;
    margin:      0;

    &::before {
        content:       '';
        width:         5px;
        height:        18px;
        border-radius: 99px;
        background:    $orange;
        flex-shrink:   0;
    }
}

// ─── 필수 · 선택 뱃지 ───────────────────────────────────────
.cid-badge {
    display:       inline-flex;
    align-items:   center;
    padding:       2px 8px;
    border-radius: 99px;
    font-size:     1.1rem;
    font-weight:   700;
    line-height:   1;
    vertical-align: middle;

    &--req   { background: rgba(218, 30, 40, .08); color: #da1e28; }
    &--opt   { background: #f0f4f8;                color: #6b7280; }
    &--multi { background: $inq-orange-10;         color: $orange; }
}

// ─── 2열 그리드 ─────────────────────────────────────────────
.cid-grid {
    display:               grid;
    grid-template-columns: 1fr 1fr;
    gap:                   16px;

    @media (max-width: 540px) { grid-template-columns: 1fr; }
}

// ─── 필드 ───────────────────────────────────────────────────
.cid-field {
    display:        flex;
    flex-direction: column;
    gap:            6px;
    &--err .cid-inp { border-color: $inq-err-bdr; background: $inq-err-bg; }
}

.cid-lbl {
    font-size:   1.4rem;
    font-weight: 700;
    color:       $inq-text-label;
}

.cid-errmsg  { font-size: 1.2rem; color: $err-clr; font-weight: 600; }

// ─── 입력 필드 ──────────────────────────────────────────────
.cid-inp {
    width:       100%;
    height:      48px;
    padding:     0 14px;
    border:      1px solid $border;
    border-radius: 7px;
    background:  #fff;
    font-size:   1.5rem;
    font-family: inherit;
    color:       $inq-text-primary;
    outline:     none;
    transition:  border-color .18s ease, box-shadow .18s ease;

    &::placeholder { color: $inq-text-ph; }

    &:focus {
        border-color: $focus-bdr;
        box-shadow:   0 0 0 4px $focus-shd;
    }

    // 텍스트에어리어
    &--ta {
        height:      auto;
        padding:     12px 14px;
        resize:      vertical;
        line-height: 1.6;
        min-height:  100px;
    }
}

// ─── 옵션 버튼 (라디오 / 체크박스 스타일) ───────────────────
.cid-opt-wrap {
    display: grid;
    gap:     8px;

    // 산업 분야: 4열 (8개 항목)
    &--4col {
        grid-template-columns: repeat(4, 1fr);

        @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
    }

    // 도입 항목: 2열 (긴 레이블)
    &--2col {
        grid-template-columns: repeat(2, 1fr);

        @media (max-width: 540px) { grid-template-columns: 1fr; }
    }
}

.cid-opt-col {
    display:        flex;
    flex-direction: column;
    gap:            8px;
}

.cid-opt {
    display:     inline-flex;
    align-items: center;
    gap:         8px;
    padding:     0 14px;
    height:      48px;
    border:      1px solid $border;
    border-radius: 7px;
    background:  #fff;
    font-size:   1.35rem;
    font-weight: 600;
    color:       $inq-text-label;
    cursor:      pointer;
    user-select: none;
    text-align:  left;
    transition:  border-color .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease;

    // 앞 마커 (라디오/체크 표시용)
    &::before {
        content:       '';
        width:         16px;
        height:        16px;
        flex-shrink:   0;
        border:        1.5px solid $inq-opt-mark;
        background:    #fff;
        transition:    border-color .15s ease, background .15s ease;
    }

    &--radio::before { border-radius: 50%; }
    &--check::before { border-radius: 4px; }

    // 체크 버튼: 레이블이 길어 줄바꿈 발생 시 높이 자동 확장
    &--check {
        height:     auto;
        min-height: 48px;
        padding:    12px 14px;
    }

    // 전체 너비 블록 (도입 의향 전용)
    &--block {
        width:          100%;
        justify-content: flex-start;
        padding:        0 16px;
        height:         52px;
    }

    // 활성 상태
    &--on {
        border-color: $active-bdr;
        background:   $active-bg;
        color:        $active-clr;
        box-shadow:   0 0 0 3px $inq-orange-08;
    }

    &--radio.cid-opt--on::before {
        border-color: $orange;
        background:   $orange;
        box-shadow:   inset 0 0 0 3px #fff;
    }

    &--check.cid-opt--on::before {
        border-color:      $orange;
        background-color:  $orange;
        background-image:  url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'%3E%3Cpath d='M1 5L4.5 8.5L11 1' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
        background-size:   9px 8px;
        background-position: center;
        background-repeat: no-repeat;
    }

    &:hover:not(.cid-opt--on) {
        border-color: $inq-hover-bdr;
        background:   $inq-hover-bg;
    }
}

// ─── 개인정보 동의 ──────────────────────────────────────────
.cid-agree {
    display:     flex;
    align-items: center;
    gap:         12px;
    padding:     16px 20px;
    border:      1px solid $inq-border-divider;
    border-radius: 10px;
    background:  $inq-agree-bg;
}

.cid-agree__box {
    flex-shrink:     0;
    width:           22px;
    height:          22px;
    border:          1.5px solid $border;
    border-radius:   5px;
    background:      #fff;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    transition:      border-color .15s ease, background .15s ease;

    &--on {
        border-color: $orange;
        background:   $orange;
    }
}

.cid-agree__txt {
    font-size:   1.4rem;
    color:       $inq-text-label;
    line-height: 1.5;
    margin:      0;
}

.cid-agree__link {
    color:           $inq-text-link;
    font-weight:     700;
    text-decoration: underline;

    &:hover { color: $inq-text-link-hv; }
}

// ─── 푸터 ───────────────────────────────────────────────────
.cid-ft {
    flex-shrink:  0;
    padding:      16px 40px;
    border-top:   1px solid $inq-border-divider;
    background:   #fff;
}

// 제출 실패 안내 (§3.4)
.cid-submit-err {
    margin:      0 0 10px;
    font-size:   1.3rem;
    font-weight: 600;
    color:       $err-clr;
    text-align:  center;
}

.cid-submit {
    width:           100%;
    height:          52px;
    border:          none;
    border-radius:   10px;
    background:      $blue-dark;
    color:           #fff;
    font-size:       1.6rem;
    font-weight:     700;
    font-family:     inherit;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    gap:             8px;
    transition:      background .18s ease, transform .1s ease;

    &:hover:not(:disabled) { background: #162e6d; }
    &:active:not(:disabled) { transform: scale(.99); }

    &:disabled {
        background: #b8c0cc;
        cursor:     not-allowed;
    }
}

.cid-submit__spinner {
    width:          18px;
    height:         18px;
    border:         2.5px solid rgba(255, 255, 255, .3);
    border-top-color: #fff;
    border-radius:  50%;
    animation:      cid-spin .7s linear infinite;
}

@keyframes cid-spin { to { transform: rotate(360deg); } }

// ─── 반응형 ─────────────────────────────────────────────────
@media (max-width: 767px) {
    .cid-panel { width: 100%; min-width: unset; }
    .cid-hd    { padding: 16px 20px 12px; }
    .cid-body  { padding: 20px 20px 16px; }
    .cid-ft    { padding: 12px 20px; }
    .cid-sec   { gap: 12px; margin-bottom: 28px; }
}
</style>
